import type { TFunction } from 'i18next';

/**
 * Sanitize a cell value to prevent CSV injection.
 * Cells starting with =, +, -, @ are prefixed with a single quote.
 */
function sanitizeCell(value: string): string {
  if (/^[=+\-@]/.test(value)) {
    return `'${value}`;
  }
  // Escape double quotes by doubling them
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function toCsvRow(values: (string | number)[]): string {
  return values.map((v) => sanitizeCell(String(v))).join(',');
}

function downloadCsv(filename: string, csvContent: string): void {
  const BOM = '\uFEFF'; // UTF-8 BOM for Excel compatibility
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

interface PayoutForExport {
  period: string;
  startDate: string;
  endDate: string;
  totalPayinAmount: number;
  totalPayoutAmount: number;
  payinSharingAmount: number;
  payoutSharingAmount: number;
  status: string;
  paymentMethod: string | null;
  lines: Array<{
    subSellerName: string;
    payinSharePercent: number;
    payoutSharePercent: number;
    payinAmount: number;
    payoutAmount: number;
  }>;
}

export function exportPayoutCsv(payouts: PayoutForExport[], t: TFunction): void {
  const headers = [
    t('payouts.period'),
    t('payouts.periodStart'),
    t('payouts.periodEnd'),
    t('transactions.calcPayinFee'),
    t('transactions.calcPayoutFee'),
    t('payouts.payinSharing'),
    t('payouts.payoutSharing'),
    t('payouts.status'),
    t('payouts.paymentMethod'),
    t('payouts.subSellerBreakdown'),
  ];

  const rows = payouts.map((p) => {
    const breakdown = p.lines
      .map((l) => `${l.subSellerName}: payin ${l.payinSharePercent}%=${l.payinAmount.toFixed(2)} payout ${l.payoutSharePercent}%=${l.payoutAmount.toFixed(2)}`)
      .join('; ');

    return toCsvRow([
      p.period,
      p.startDate,
      p.endDate,
      p.totalPayinAmount.toFixed(2),
      p.totalPayoutAmount.toFixed(2),
      p.payinSharingAmount.toFixed(2),
      p.payoutSharingAmount.toFixed(2),
      p.status,
      p.paymentMethod || '-',
      breakdown,
    ]);
  });

  const csvContent = [toCsvRow(headers as string[]), ...rows].join('\n');
  downloadCsv(`payouts_${new Date().toISOString().split('T')[0]}.csv`, csvContent);
}

interface SubSellerPayoutForExport {
  period: string;
  startDate: string;
  endDate: string;
  myPayinShare: number;
  myPayoutShare: number;
  myPayinSharePercent: number;
  myPayoutSharePercent: number;
  status: string;
}

export function exportSubSellerPayoutCsv(
  payouts: SubSellerPayoutForExport[],
  t: TFunction,
): void {
  const headers = [
    t('payouts.period'),
    t('payouts.periodStart'),
    t('payouts.periodEnd'),
    t('sellers.payinFee') + ' %',
    t('transactions.calcPayinFee'),
    t('sellers.payoutFee') + ' %',
    t('transactions.calcPayoutFee'),
    t('payouts.status'),
  ];

  const rows = payouts.map((p) =>
    toCsvRow([
      p.period,
      p.startDate,
      p.endDate,
      `${p.myPayinSharePercent}%`,
      p.myPayinShare.toFixed(2),
      `${p.myPayoutSharePercent}%`,
      p.myPayoutShare.toFixed(2),
      p.status,
    ]),
  );

  const csvContent = [toCsvRow(headers as string[]), ...rows].join('\n');
  downloadCsv(`my_payouts_${new Date().toISOString().split('T')[0]}.csv`, csvContent);
}
